import { Helmet } from "react-helmet";
import { Layout } from "@/components/layout/Layout";

export default function TermsOfServicePage() {
  return (
    <Layout>
      <Helmet>
        <title>Terms of Service | StayMiddle</title>
        <meta name="description" content="StayMiddle Terms of Service" />
      </Helmet>
      <div className="container py-12">
        <div className="max-w-4xl mx-auto prose prose-gray">
          <h1 className="mb-10 text-4xl font-bold text-center">StayMiddle Terms of Service</h1>
          <hr className="my-8" />

          <section className="mb-8">
            <h2>1. Acceptance of Terms</h2>
            <p>
              Welcome to StayMiddle. These Terms of Service (“Terms”) govern your access to and use of the StayMiddle website, mobile applications, and services (collectively, the “Platform”).
            </p>
            <p>
              By accessing or using the Platform, you agree to be bound by these Terms. If you do not agree, you may not access or use the Platform. These Terms constitute a legally binding agreement between you and StayMiddle.
            </p>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>2. Definitions</h2>
            <ul>
              <li><strong>“User”</strong> – Any individual who accesses or uses the Platform.</li>
              <li><strong>“Property Manager”</strong> – Individuals or entities who list properties on the Platform.</li>
              <li><strong>“Agent”</strong> – Individuals authorized to promote properties through unique links with their own markup.</li>
              <li><strong>“Guest”</strong> – Individuals who book properties through the Platform.</li>
              <li><strong>“Listing”</strong> – A property advertised on the Platform.</li>
              <li><strong>“Booking”</strong> – A reservation of a property through the Platform.</li>
              <li><strong>“HostCover”</strong> – StayMiddle’s platform-backed guest damage protection feature.</li>
            </ul>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>3. Account Registration</h2>
            <p>
              Users must register for an account to access core Platform functions. You agree to provide accurate, complete, and up-to-date information. You are solely responsible for maintaining the confidentiality of your credentials and any activities under your account.
            </p>
            <p>
              StayMiddle may suspend or terminate any account found to be fraudulent, incomplete, or misleading.
            </p>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>4. User Roles and Responsibilities</h2>
            <h3>4.1 Property Managers</h3>
            <ul>
              <li>Provide accurate listings and availability</li>
              <li>Maintain the condition and legality of the listed properties</li>
              <li>Honor accepted bookings</li>
              <li>Comply with local laws, permits, and licensing requirements</li>
              <li>Sync calendars accurately to prevent double bookings</li>
              <li>Accept bookings made through agents' customized links</li>
              <li>Respect and cooperate with StayMiddle's damage protection policy and procedures</li>
            </ul>
            <h3>4.2 Agents</h3>
            <ul>
              <li>Promote listings with integrity and transparency</li>
              <li>Set fair markup percentages</li>
              <li>Use only approved promotional tools (such as referral links)</li>
              <li>Avoid false claims or impersonation</li>
              <li>Acknowledge that they do not own the properties they promote</li>
            </ul>
            <h3>4.3 Guests</h3>
            <ul>
              <li>Provide truthful information when booking</li>
              <li>Treat properties with care and adhere to property rules</li>
              <li>Report damages or issues promptly</li>
              <li>Respect payment, cancellation, and check-in/out policies</li>
            </ul>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>5. Listings and Bookings</h2>
            <p>
              StayMiddle acts solely as a technology platform and intermediary. We do not own, operate, or control the properties listed.
            </p>
            <p>
              All bookings made through the Platform represent a direct agreement between the Guest and Property Manager. StayMiddle facilitates the transaction and holds no liability beyond the role defined herein.
            </p>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>6. Fees and Payments</h2>
            <ul>
              <li><strong>Service Fees:</strong> Guests may be charged a service fee on each booking.</li>
              <li><strong>Platform Fees:</strong> Property Managers agree to a commission on each booking facilitated via StayMiddle.</li>
              <li><strong>Agent Commission:</strong> Agents earn commission through their markup on listings shared via their referral link.</li>
              <li>All payments are processed securely via Stripe. Users agree to Stripe's terms and authorize StayMiddle to distribute payments accordingly.</li>
              <li>StayMiddle may deduct platform and agent fees prior to disbursing payouts.</li>
            </ul>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>7. Cancellations and Refunds</h2>
            <ul>
              <li>Property Managers define cancellation policies on their listings.</li>
              <li>Refunds to Guests are based on the selected policy.</li>
              <li>StayMiddle reserves the right to issue refunds if:</li>
              <ul>
                <li>The listing is materially misrepresented</li>
                <li>The property is unsafe or uninhabitable</li>
                <li>The booking is canceled by the Property Manager</li>
              </ul>
            </ul>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>8. StayMiddle HostCover (Damage Protection)</h2>
            <h3>8.1 Coverage Overview:</h3>
            <p>All bookings made through the Platform are automatically protected under HostCover, StayMiddle's guest damage protection program.</p>
            <ul>
              <li><strong>Coverage Limit:</strong> Up to AED 10,000 per booking</li>
              <li><strong>Covered:</strong> Accidental guest-related damage to property or furnishings</li>
              <li><strong>Not Covered:</strong> Wear and tear, lost personal items, cash, or damages due to negligence</li>
              <li><strong>Eligibility:</strong> Applies only to confirmed, paid bookings made directly through the Platform</li>
              <li><strong>Claim Deadline:</strong> Claims must be submitted within 48 hours of guest checkout with supporting documentation (photos, receipts, etc.)</li>
            </ul>
            <h3>8.2 Disclaimer:</h3>
            <p>
              HostCover is not an insurance policy. It is a platform-backed discretionary protection benefit. StayMiddle reserves the right to investigate all claims and deny coverage in cases of fraud, misuse, or insufficient evidence.
            </p>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>9. Prohibited Activities</h2>
            <ul>
              <li>Circumvent payment systems</li>
              <li>List properties they don't have authority over</li>
              <li>Post false or misleading listings</li>
              <li>Harass or impersonate others</li>
              <li>Book under false identity or purpose</li>
              <li>Interfere with Platform systems, security, or availability</li>
            </ul>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>10. Intellectual Property</h2>
            <p>
              All content, logos, trademarks, and design assets on the Platform are owned by StayMiddle or licensed for use. Users may not copy, reproduce, or distribute content without express permission.
            </p>
            <p>
              By submitting content (photos, descriptions, etc.), you grant StayMiddle a non-exclusive license to use that content for the promotion of the property or Platform.
            </p>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>11. Limitation of Liability</h2>
            <p>StayMiddle is not liable for:</p>
            <ul>
              <li>Guest behavior or damages not covered under HostCover</li>
              <li>Unavailable listings, double bookings, or system outages</li>
              <li>Third-party services (Stripe, Airbnb, etc.)</li>
              <li>Any indirect or consequential losses (including revenue or reputation)</li>
            </ul>
            <p>
              Total liability in any case shall not exceed the amount paid to StayMiddle in the 3 months prior to the incident.
            </p>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>12. Indemnification</h2>
            <p>You agree to indemnify and hold harmless StayMiddle, its directors, officers, employees, and affiliates from any claims, damages, or losses arising from:</p>
            <ul>
              <li>Your use of the Platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of laws or third-party rights</li>
            </ul>
          </section>

          <hr className="my-8" />

          <section className="mb-8">
            <h2>13. Modifications to Terms</h2>
            <p>
              StayMiddle may update these Terms at any time. Changes will be posted on the Platform. Continued use of the Platform after updates constitutes acceptance of the new Terms.
            </p>
          </section>
          
          <hr className="my-8" />

          <section className="mb-8">
            <h2>14. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the United Arab Emirates. All legal matters shall fall under the jurisdiction of Dubai, UAE.
            </p>
          </section>
          
          <hr className="my-8" />

          <section className="mb-8">
            <h2>15. Dispute Resolution</h2>
            <ul>
              <li>Parties agree to attempt informal resolution first</li>
              <li>If unresolved, the matter shall be referred to arbitration under the Dubai International Arbitration Centre (DIAC)</li>
            </ul>
          </section>
          
          <hr className="my-8" />

          <section className="mb-8">
            <h2>16. Contact Information</h2>
            <address>
              StayMiddle FZ-LLC<br />
              Dubai, United Arab Emirates<br />
              Email: <a href="mailto:legal@staymiddle.com">legal@staymiddle.com</a>
            </address>
          </section>
        </div>
      </div>
    </Layout>
  );
}